<template>
  <!-- Advertisement Media Content -->
  <div
    class="relative flex flex-col" style="min-height: 60px"
    :style="{
      ...(advertisement.publisher_platform?.includes('landing page') ? { height: '550px' } : {}),
      ...(forceSquareRatio ? { aspectRatio: '1 / 1' } : {})
    }"
    :class="[
      { 'h-96 overflow-y-scroll': advertisement.publisher_platform?.includes('landing page') },
      { 'small-width  mx-auto bg-background-hover': shrinkMedia },
      { 'relative': ['dco', 'dpa'].includes(advertisement.type) },
      { 'overflow-hidden': forceSquareRatio }
    ]"
  >
    <!-- Missing video -->
    <div v-if="lensMetaVideoMissing && !showLensIframeFallback" class="missing-video-banner p-2">
      <div>
        <BaseText type="label" size="xs" class="text-white">Missing Video URL</BaseText>
        <BaseText class="text-neutral-alpha-650" size="xs">Error loading video from Meta</BaseText>
      </div>
      <button v-if="advertisement.fallbackVideo" class="external-btn" style="background-color: #3B383D" @click="openExternalSrc">
        <OpenExternalIcon />
      </button>
    </div>

    <!-- Loading indicator -->
    <transition>
      <div v-if="loadingMedia && !showError" class="absolute left-0 right-0 top-0 bottom-0 flex justify-center items-center z-10">
        <BaseLoadingLogo />
      </div>
    </transition>

    <!-- Dynamic Indicator -->
    <transition>
      <div v-if="(['dco', 'dpa'].includes(advertisement.type)) && showDynamicIndicator && !loadingMedia"
      class="absolute top-3 left-4 flex items-center gap-1 w-max bg-black bg-opacity-30 z-10 py-1 px-1.5 rounded cursor-none pointer-events-none">
        <DynamicFormatIcon :width="15" :height="15" stroke="#fff"/>
        <BaseText type="label" size="xs" class="text-white">
          Dynamic Ad ({{ carouselIndex + 1 }}/{{ advertisement.cards.length }})
        </BaseText>
      </div>
    </transition>

    <!-- Lens iframe fallback -->
    <div v-if="showLensIframeFallback" id="lens-iframe-fallback" class="overflow-hidden rounded-lg" v-html="lensIframeFallbackHtml" />

    <!-- Locked State Content Preview -->
    <div 
      v-else-if="locked && !showError"
      class="relative w-full transition-opacity duration-100"
      :class="[
        { 'h-full': forceSquareRatio },
        loadingMedia ? 'opacity-0' : 'opacity-100'
      ]"
    >
      <BaseBlurTabOverlay
        class="w-full" :class="{ 'h-full': forceSquareRatio }"
        :options="{ borderRadius: rounded ? '8px' : '0px' }"
      >
        <BaseAdvertisementStaticPreview 
          :advertisement="advertisement" 
          :class="[
            advertisement.publisher_platform?.includes('landing page') ? '' : (forceSquareRatio ? 'object-cover' : 'object-contain'),
            { 'rounded-lg': rounded }
          ]"
          @loaded-content="loadingMedia = false, $emit('loaded-content')"
          @error="showError = true"
        />
      </BaseBlurTabOverlay>
    </div>
    
    <!-- Image -->
    <img
      v-else-if="advertisement.type === 'image' && !showError"
      ref="media"
      alt="Image"
      :src="advertisement.image"
      class="w-full transition-opacity duration-100"
      :class="[
        advertisement.publisher_platform?.includes('landing page') ? '' : (forceSquareRatio ? 'object-cover' : 'object-contain'),
        { 'rounded-lg': rounded },
        { 'h-full': forceSquareRatio },
        loadingMedia ? 'opacity-0' : 'opacity-100'
      ]"
      @load="loadingMedia = false, $emit('loaded-content')"
      @error="showError = true"
    >

    <!-- Image Error -->
    <div v-else-if="showError || !advertisement.type"
    class="flex flex-col items-center justify-center h-full w-full" :class="{'rounded-lg': rounded}"
    style="min-height: 186px">
      <InfoIcon class="text-icon-disabled" :width="32" :height="32" />
      <BaseText type="label" size="sm" class="text-text-subdued mt-1.5">
        Media Error
      </BaseText>
    </div>

    <!-- Video Player -->
    <BaseAdvertisementVideoPlayer
      v-else-if="advertisement.type === 'video'"
      ref="videoPlayer"
      :advertisement="advertisement"
      rounded
      :contain-media="forceSquareRatio"
      :object-cover="forceSquareRatio"
      class="transition-opacity duration-100"
      :class="[
        loadingMedia ? 'opacity-0' : 'opacity-100',
        { 'h-full w-full': forceSquareRatio }
      ]"
      :in-ad-details-drawer="inAdDetailsDrawer"
      @loaded-thumbnail="loadingMedia = false, $emit('loaded-content')"
      @loaded-content="loadingMedia && (loadingMedia = false), $emit('loaded-content')"
      @error="showError = true"
    />

    <!-- Carousel -->
    <BaseCarousel
      v-else
      ref="carousel"
      :identifier="advertisement.id"
      :items="advertisement.cards"
      :carousel-index.sync="carouselIndex"
      :keep-height-constant="!inAdDetailsDrawer"
      :contain-media="forceSquareRatio"
      :object-cover="forceSquareRatio"
      rounded
      class="transition-opacity duration-100"
      :class="[
        loadingMedia ? 'opacity-0' : 'opacity-100',
        { 'h-full w-full': forceSquareRatio }
      ]"
      @loaded-content="loadingMedia = false, $emit('loaded-content')"
      @mouseenter.native="showDynamicIndicator = true"
      @mouseleave.native="showDynamicIndicator = false"
      @error="showError = true"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ClickOutside from 'vue-click-outside'

import DynamicFormatIcon from '../globals/Icons/FilterIcons/DynamicFormatIcon.vue'
import OpenExternalIcon from '../globals/Icons/OpenExternalIcon.vue'
import InfoIcon from '../globals/Icons/InfoSimpleIcon.vue'

export default {
  name: 'AdvertisementMediaContent',
  components: {
    DynamicFormatIcon,
    OpenExternalIcon,
    InfoIcon
  },
  directives: {
    ClickOutside
  },
  props: {
    advertisement: {
      type: Object,
      default: () => {}
    },
    shrinkMedia: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    inAdDetailsDrawer: {
      type: Boolean,
      default: false
    },
    showLensIframeFallback: {
      type: Boolean,
      default: false
    },
    lensIframeFallbackHtml: {
      type: String,
      default: ''
    },
    forceSquareRatio: {
      type: Boolean,
      default: false
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showError: false,
      carouselIndex: 0,
      showDynamicIndicator: false,
      loadingMedia: false,
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getTheme']),
    lensMetaVideoMissing () {
      return this.inAdDetailsDrawer && this.advertisement.isLens && this.advertisement.isVideoMissing
    },
  },
  watch: {
    carouselIndex () {
      this.$emit('update:carousel-index', this.carouselIndex)
      this.$nextTick(() => {
        this.resize()
      })
    },
  },
  mounted () {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    if (isSafari) {
      this.safariBrowser = true
      this.showThumbnail = false
    }
  },
  methods: {
    openExternalSrc () {
      window.open(this.advertisement.fallbackVideo, '_blank')
    },
    resize () {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    playVideoAtTimestamp (time) {
      // This func is called directly on the component instance from a parent
      if (this.advertisement?.type === 'video' && this.advertisement.video) {
        const videoPlayer = this.$refs.videoPlayer
        videoPlayer.playVideoAtTimestamp(time)
      } else if (['carousel', 'dco'].includes(this.advertisement?.type) && this.advertisement?.cards?.[this.carouselIndex]?.video) {
        const carousel = this.$refs.carousel
        carousel.playVideoAtTimestamp(time)
      }
    },
    setCarouselIndex (index) {
      // This func is called directly on the component instance from a parent
      const carousel = this.$refs.carousel
      if (carousel) {
        carousel.setCarouselIndex(index)
      }
    }
  }
}
</script>

<style scoped>
.small-width {
  max-width: 200px;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.missing-video-banner{
  border-radius: 6px;
  z-index: 50;
  width: calc(100% - 20px);
  background: rgba(6, 7, 16, 0.88);
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: absolute;
  left:10px;
  bottom:10px;
}
.external-btn{
  border-radius: 6px;
  padding: 6px;
  color: white;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), rgba(255, 255, 255, 0.12);
  background: linear-gradient(180deg, color(display-p3 1 1 1 / 0.00) 0%, color(display-p3 1 1 1 / 0.08) 100%), color(display-p3 1 1 1 / 0.12);

  /* Button/Secondary/Normal */
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  box-shadow: 0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.13), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.08);
}
</style>
